'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExtraWorkOrdersFilters } from '../../hooks/useExtraWorkOrdersFilters';

interface ExtraWorkOrdersFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

export const ExtraWorkOrdersFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: ExtraWorkOrdersFiltersPanelProps) => {
  const { t } = useTranslation('extraWorkOrdersModule');

  const {
    filters,
    handleStatusChange,
    handleTeamIdChange,
    handleEntryIdChange,
    handleUserIdChange,
    handleLocationIdChange,
    handleStartedAtChange,
    handleFinishedAtChange,
    handleDeadlineAtChange,
    handleCreatedAtChange,
    clearAllFilters,
  } = useExtraWorkOrdersFilters({ initialConditions, onChange });

  const statusOptions = useMemo(
    () => [
      { value: 'PENDING', label: t('status.PENDING') },
      { value: 'IN_PROGRESS', label: t('status.IN_PROGRESS') },
      { value: 'DONE', label: t('status.DONE') },
    ],
    [t],
  );

  const [localTeamId, setLocalTeamId] = useState(filters.team_id);
  const [localEntryId, setLocalEntryId] = useState(filters.entry_id);
  const [localUserId, setLocalUserId] = useState(filters.user_id);
  const [localLocationId, setLocalLocationId] = useState(filters.location_id);

  useEffect(() => {
    setLocalTeamId(filters.team_id);
    setLocalEntryId(filters.entry_id);
    setLocalUserId(filters.user_id);
    setLocalLocationId(filters.location_id);
  }, [filters.team_id, filters.entry_id, filters.user_id, filters.location_id]);

  const applyIdFilter = (key: 'team_id' | 'entry_id' | 'user_id' | 'location_id', value: string) => {
    if (key === 'team_id') handleTeamIdChange(value);
    if (key === 'entry_id') handleEntryIdChange(value);
    if (key === 'user_id') handleUserIdChange(value);
    if (key === 'location_id') handleLocationIdChange(value);
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status.length > 0) count++;
    if (filters.team_id.trim()) count++;
    if (filters.entry_id.trim()) count++;
    if (filters.user_id.trim()) count++;
    if (filters.location_id.trim()) count++;
    if (filters.started_at?.from && filters.started_at?.to) count++;
    if (filters.finished_at?.from && filters.finished_at?.to) count++;
    if (filters.deadline_at?.from && filters.deadline_at?.to) count++;
    if (filters.created_at?.from && filters.created_at?.to) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <FilterSection title={t('filters.basic_info', 'Información básica')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.status')}>
            <MultiSelect
              options={statusOptions}
              value={filters.status}
              onValueChange={handleStatusChange}
              placeholder={t('filters.select_status', 'Seleccionar estado')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-status"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.team_id')}>
            <input
              type="text"
              inputMode="numeric"
              value={localTeamId}
              onChange={(e) => setLocalTeamId(e.target.value)}
              onBlur={() => applyIdFilter('team_id', localTeamId)}
              onKeyDown={(e) => e.key === 'Enter' && applyIdFilter('team_id', localTeamId)}
              placeholder={t('filters.team_id_placeholder', 'ID equipo')}
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-team-id"
            />
          </FilterField>

          <FilterField label={t('filters.entry_id')}>
            <input
              type="text"
              inputMode="numeric"
              value={localEntryId}
              onChange={(e) => setLocalEntryId(e.target.value)}
              onBlur={() => applyIdFilter('entry_id', localEntryId)}
              onKeyDown={(e) => e.key === 'Enter' && applyIdFilter('entry_id', localEntryId)}
              placeholder={t('filters.entry_id_placeholder', 'ID entrada')}
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-entry-id"
            />
          </FilterField>

          <FilterField label={t('filters.user_id')}>
            <input
              type="text"
              inputMode="numeric"
              value={localUserId}
              onChange={(e) => setLocalUserId(e.target.value)}
              onBlur={() => applyIdFilter('user_id', localUserId)}
              onKeyDown={(e) => e.key === 'Enter' && applyIdFilter('user_id', localUserId)}
              placeholder={t('filters.user_id_placeholder', 'ID usuario')}
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-user-id"
            />
          </FilterField>

          <FilterField label={t('filters.location_id')}>
            <input
              type="text"
              inputMode="numeric"
              value={localLocationId}
              onChange={(e) => setLocalLocationId(e.target.value)}
              onBlur={() => applyIdFilter('location_id', localLocationId)}
              onKeyDown={(e) => e.key === 'Enter' && applyIdFilter('location_id', localLocationId)}
              placeholder={t('filters.location_id_placeholder', 'ID ubicación')}
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-location-id"
            />
          </FilterField>
        </div>
      </FilterSection>

      <FilterSection title={t('filters.dates_section', 'Fechas')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.started_at')}>
            <DateRangePicker
              initialDateFrom={filters.started_at?.from}
              initialDateTo={filters.started_at?.to}
              onUpdate={({ range }) => handleStartedAtChange(range)}
              placeholder={t('filters.date_placeholder', 'Seleccionar rango')}
              dataTestId="filter-started-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          <FilterField label={t('filters.finished_at')}>
            <DateRangePicker
              initialDateFrom={filters.finished_at?.from}
              initialDateTo={filters.finished_at?.to}
              onUpdate={({ range }) => handleFinishedAtChange(range)}
              placeholder={t('filters.date_placeholder', 'Seleccionar rango')}
              dataTestId="filter-finished-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          <FilterField label={t('filters.deadline_at')}>
            <DateRangePicker
              initialDateFrom={filters.deadline_at?.from}
              initialDateTo={filters.deadline_at?.to}
              onUpdate={({ range }) => handleDeadlineAtChange(range)}
              placeholder={t('filters.date_placeholder', 'Seleccionar rango')}
              dataTestId="filter-deadline-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          <FilterField label={t('filters.created_at')}>
            <DateRangePicker
              initialDateFrom={filters.created_at?.from}
              initialDateTo={filters.created_at?.to}
              onUpdate={({ range }) => handleCreatedAtChange(range)}
              placeholder={t('filters.date_placeholder', 'Seleccionar rango')}
              dataTestId="filter-created-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </FilterSection>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            {t('filters.clear')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">{children}</div>
);

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
